import {
  GutBodyConnectionsColorVariant,
  GutBodyConnectionsOrgan,
} from '@repo/types';
import { GutBodyConnectionsOrganBadge, Typography } from '@repo/ui';
import useIsSensitiveUnacknowledged from 'hooks/acknowledgments/useIsSensitiveUnacknowledged';
import React, { useMemo } from 'react';
import { organAnchorId } from 'views/Results/utils/organs';
import FlaggedMetricsCount from '../FlaggedMetricsCount';
import ConditionLinkCard from './ConditionLinkCard';
import ConditionMarkersSection from './ConditionMarkersSection';
import styles from './OrganSection.module.css';
import { sortConditionsReportedFirst } from './utils/sortConditions';

type OrganSectionProps = {
  organ: GutBodyConnectionsOrgan;
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  colorVariant?: GutBodyConnectionsColorVariant;
  style?: React.CSSProperties;
};

export default function OrganSection({
  organ,
  age,
  pregnant,
  colorVariant,
  style,
}: OrganSectionProps) {
  const conditions = useMemo(
    () => sortConditionsReportedFirst(organ.conditions),
    [organ.conditions],
  );

  const isSensitiveUnacknowledged = useIsSensitiveUnacknowledged();

  return (
    <div
      className={styles.container}
      style={style}
      id={organAnchorId(organ.lookup_code)}
    >
      <div className={styles.header}>
        <GutBodyConnectionsOrganBadge
          organ={organ}
          age={age}
          pregnant={pregnant}
          colorVariant={colorVariant}
        />
        <Typography variant='heading-m'>{organ.display_name}</Typography>
      </div>
      {!!conditions.length ? (
        <div className={styles.conditionsCards}>
          {conditions.map(condition => (
            <ConditionLinkCard
              key={condition.id}
              name={condition.display_name}
              href={condition.condition}
              markedInSurvey={condition.marked_in_survey}
              analyticsEventName='Condition Card Clicked'
              analyticsProperties={{
                condition_name: condition.display_name,
                condition: condition.condition,
                marked_in_survey: condition.marked_in_survey,
                experience: 'gut_body_connections',
              }}
              trailing={
                <FlaggedMetricsCount
                  flagged={condition.flagged_count}
                  total={condition.total_count}
                  sensitive={isSensitiveUnacknowledged(
                    condition.id,
                    condition.sensitive,
                  )}
                />
              }
            />
          ))}
        </div>
      ) : null}
      <ConditionMarkersSection markers={organ.markers} />
    </div>
  );
}
